import * as Cookies from 'js-cookie'

import axios from 'axios'
import { downloadFile } from './dom'
import { getFilters } from './filters'
import logger from '@/utils/logger'
import { login, getAccessToken } from '@/auth/AuthService'
import qs from '@funkybob/qs'
import { setupCache, buildMemoryStorage } from 'axios-cache-interceptor'
import { getStoredUser } from '@/utils'

// Notify helper — uses the global app instance set in main.js
function notify (options) {
  const app = window.__vue_app__
  if (app && app.config.globalProperties.$notify) {
    app.config.globalProperties.$notify(options)
  } else {
    console.warn('[notify] App not ready or $notify not available', options)
  }
}

const LOG = logger('utils request')

// from a given request, form the cache key to be used to cache data
const getCacheKey = (url, params) => {
  const { active_client = '' } = getStoredUser() || {}
  const key = `${active_client}|${url}` + qs.encode(params || {})
  LOG.debug(`Using cache key ${key}`)
  return key
}

// storage instance for cached api responses
const storage = buildMemoryStorage()

// create an axios instance
const instance = axios.create({
  timeout: 60000, // request timeout in milliseconds
  paramsSerializer: { serialize: qs.encode }
})

// wrap with cache interceptor
const service = setupCache(instance, {
  storage,
  ttl: 1 * 60 * 60 * 1000, // 1 hour
  generateKey: req => getCacheKey(req.url, req.params),
  debug: import.meta.env.DEV ? console.log : undefined
})

// request interceptor
service.interceptors.request.use(
  async config => {
    // strip cache param from request parameters and control caching
    if (config.params) {
      const { cache: useCache = true, ...params } = config.params
      config.params = params
      if (useCache !== true) {
        config.cache = false
      }
    } else {
      config.cache = false
    }

    // add csrftoken for posts
    if (['post', 'delete', 'patch', 'put'].indexOf(config.method) !== -1) {
      config.headers['X-CSRFToken'] = Cookies.get('csrftoken');
    }
    // add authorization token from the Auth0 SDK token cache.
    // The SDK returns a cached token when still valid and silently
    // refreshes via the refresh token when expired.
    try {
      const token = await getAccessToken()
      if (token) {
        config.headers['Authorization'] = 'Bearer ' + token;
      }
    } catch (_) {
      // If token retrieval fails the request will go out without an
      // Authorization header. The 401 response handler will trigger
      // a fresh login.
    }
    return config
  }
)

// how to handle error codes
const statusHandlers = {
  400: (resp) => {
    // form errors should be handled by form, we attempt to extract the information from the json blob
    const errors: Record<string, any> = {}
    try {
      Object.keys(resp.data).map(key => {
        errors[key] = resp.data[key].map((error: any) => error.message).join(', ')
      })
      resp.data = errors
    } catch (_error) {
      console.warn('Unhandled server error')
    }
    return resp
  },
  401: (resp) => {
    login()
    return Promise.reject(new Error(resp.statusText))
  },
  403: (resp) => {
    notify({
      group: 'notifications',
      title: 'Not Allowed',
      type: 'warn',
      text: 'You do not have permissions for this action.'
    })
    return Promise.reject(new Error(resp.statusText))
  },
  409: (resp) => {
    // Conflict — the server rejected the change with an actionable reason
    // (e.g. a duplicate name). Surface its `error` message to the user.
    notify({
      group: 'notifications',
      title: 'Could not save',
      type: 'warn',
      duration: 6000,
      text: resp.data?.error || 'That change conflicts with existing data.'
    })
    return Promise.reject(new Error(resp.statusText))
  },
  500: (resp) => {
    LOG.warn('Server error:', resp.config?.url, resp.status)
    // Resolve with an axios-shaped response whose BODY is
    // {data: null}, not whose body is null. service.json unwraps
    // `.data` from the axios response, so without the extra
    // wrapping layer the caller of service.json / service.filtered
    // receives bare `null` and a destructure like
    //   .then(({ data }) => …)
    // crashes with "Cannot destructure property 'data' of …".
    // With the wrap, the caller receives {data: null} — a
    // body-shaped value matching the backend's normal envelope —
    // and the destructure yields data === null, which call sites
    // either render as empty or fall through their nullish checks.
    return Promise.resolve({ data: { data: null } })
  }
}

// response interceptor
service.interceptors.response.use(
  resp => resp,
  error => {
    const resp = error.response
    // No response at all — request never got a header back.
    // Common causes:
    //   * upstream gateway timeout (502/504 from the load
    //     balancer with no body bubbled up to axios)
    //   * client lost network mid-flight
    //   * request was cancelled
    // Pre-fix this crashed at `resp.status` and surfaced as
    // "Cannot read properties of undefined (reading 'status')"
    // in the browser console.
    if (!resp) {
      LOG.warn('Network/gateway error (no response):',
        error.config?.url, error.code, error.message)
      const isTimeout = error.code === 'ECONNABORTED'
        || /timeout/i.test(error.message || '')
      notify({
        group: 'notifications',
        type: 'error',
        title: isTimeout ? 'Request timed out' : 'Connection error',
        duration: 5000,
        text: isTimeout
          ? 'The request took too long to complete. Please '
            + 'try again or narrow the date range.'
          : 'Could not reach the server. Check your '
            + 'connection and try again.',
      })
      return Promise.reject(error)
    }
    // handle status codes
    if (statusHandlers[resp.status] !== undefined) {
      return statusHandlers[resp.status](resp)
    }

    let messages
    if (resp.data && resp.data.__all__) {
      messages = resp.data.__all__.map(row => row.message).join('<br />')
    } else {
      messages = resp.data
    }

    notify({
      group: 'notifications',
      type: 'error',
      title: resp.statusText,
      duration: 5000,
      text: messages
    })
    return Promise.reject(error)
  }
)

// function to delete cached requests
const deleteCache = (fn = null, params = null) => {
  for (const [cacheKey] of storage.data) {
    if (!fn || (
      params ? cacheKey === getCacheKey(fn, params)
        : typeof fn === 'function' ? fn(cacheKey)
          : cacheKey.split('|')[1].indexOf(fn) === 0
    )) {
      storage.remove(cacheKey)
      LOG.debug('Deleted cached request', cacheKey)
    }
  }
}

// shorthand to apply store filters as query parameters
service.filtered = (url: string, config: Record<string, any> = {}) => {
  const params = {...getFilters(), ...config}
  return service.json(url, {params})
}

// shorthand to return json data
service.json = (url: string, config: Record<string, any> = {}) => {
  return service.get(url, config).then(resp => resp.data)
}

// shorthand to return file download
service.downloadFile = (url: string, config: Record<string, any> = {}) => {
  const { params, ...otherConfigs } = config
  // add filters to any params config passed
  const args = {params: {...getFilters(), ...params}, ...otherConfigs}
  return service.get(url, args).then(downloadFile)
}

export default service;
export {
  deleteCache,
  getCacheKey
}
